import { assignInlineVars } from "@vanilla-extract/dynamic";
import { scaleLinear } from "d3-scale";
import { useRef } from "react";
import * as styles from "./SimpleBarChart.css";
import type { Props } from "./types";

export default function SimpleBarChart({
	domain,
	colors,
	gap = 2,
	barHeight = 7,
	data,
	ticks = 4,
	threshold = Number.POSITIVE_INFINITY,
	className,
	min,
	max,
}: Props) {
	const chartRef = useRef<HTMLDivElement>(null);
	const minValue = min ?? Math.min(...(data ?? []).flat());
	const maxValue = max ?? Math.max(...(data ?? []).flat());
	const scale = scaleLinear(domain, [0, 100]).clamp(true);
	const isNumberGuard = (value: number | number[]): value is number =>
		typeof value === "number";
	const _ticks = isNumberGuard(ticks)
		? scale.ticks(ticks)
		: ticks.map((t) => scale(t));

	const altScale = scaleLinear(
		[minValue > 0 ? 0 : minValue, maxValue],
		[0, 100],
	);
	const boltX = scale(threshold);

	return (
		<div
			ref={chartRef}
			className={`${styles.chart} ${className}`}
			style={assignInlineVars({
				[styles.rowCount]: `${data?.length ?? 0}`,
				[styles.boltX]: `${boltX + 2}%`,
				[styles.barGap]: `${gap}px`,
			})}
		>
			<div className={styles.ticks}>
				{_ticks.map((t) => (
					<div
						key={t}
						className={styles.tick}
						style={assignInlineVars({
							[styles.tickX]: `${t}%`,
						})}
					>
						<div className={styles.tickLabel}>{scale.invert(t)}</div>
					</div>
				))}
			</div>
			<div className={styles.bars}>
				{data?.map((row, i) => {
					const rowKey = `row-${i}`;
					return (
						<div key={rowKey} className={styles.barGroup}>
							{row.map((value, j) => {
								const barKey = `${rowKey}-bar-${j}`;
								const overflow = value > threshold;
								const withBoltColumns = overflow
									? `${scale(threshold)}% 1fr`
									: "";
								const width = overflow ? altScale(value) : scale(value);
								const color = colors?.[j] ?? "black";

								return (
									<div
										key={barKey}
										className={styles.barTrack({ withBolt: value > threshold })}
										style={assignInlineVars({
											[styles.withBoltColumns]: withBoltColumns,
										})}
									>
										{overflow ? (
											<>
												<div
													className={styles.bar({ type: "filler" })}
													style={{
														backgroundColor: color,
														height: `${barHeight}px`,
													}}
												/>
												<div
													data-value={value}
													className={styles.bar()}
													style={{
														backgroundColor: color,
														width: `${width}%`,
														height: `${barHeight}px`,
													}}
												/>
											</>
										) : (
											<div
												data-value={value}
												className={styles.bar({
													type:
														Math.abs(Number(value)) > 0 ? "nonZero" : undefined,
												})}
												style={{
													backgroundColor: color,
													width: `${width}%`,
													height: `${barHeight}px`,
												}}
											/>
										)}
									</div>
								);
							})}
						</div>
					);
				})}
			</div>
		</div>
	);
}
